import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  createNewSampleZip,
  getLastSampleZipFileName,
  isSampleFsaaSupported,
  openExistingSampleZip,
  peekStoredSampleZipResume,
  resumeLastSampleZip,
  tryRestoreSampleZipSession,
} from '../../services/systems/sample-local/sampleZipFile';
import { getSampleZipApiError } from '../../services/systems/sample-local/sampleZipErrors';
import {
  LocalZipFileIdentity,
  LocalZipRecentList,
} from '../common/localZip/LocalZipFileIdentity';
import {
  listLocalZipRecent,
  migrateLegacyLastZipFileName,
  type LocalZipRecentEntry,
} from '../../utils/localZip/localZipRecentStore';
import './SampleZipGate.css';

interface SampleZipGateProps {
  onConnected: () => void;
  onError: (msg: string) => void;
}

type ZipGateAction = 'continue' | 'create' | 'open' | null;

function SampleZipGateChoice({
  action,
  busyAction,
  busy,
  primary,
  mutedBadge,
  iconClass,
  label,
  desc,
  className = '',
  onClick,
}: {
  action: Exclude<ZipGateAction, null>;
  busyAction: ZipGateAction;
  busy: boolean;
  primary?: boolean;
  mutedBadge?: boolean;
  iconClass: string;
  label: ReactNode;
  desc: ReactNode;
  className?: string;
  onClick: () => void;
}) {
  const active = busyAction === action;
  return (
    <button
      type="button"
      className={`sample-zip-gate-choice${primary ? ' sample-zip-gate-choice--primary' : ''}${
        className ? ` ${className}` : ''
      }`}
      disabled={busy}
      aria-busy={active}
      onClick={onClick}
    >
      <span
        className={`sample-zip-gate-choice__badge${mutedBadge ? ' sample-zip-gate-choice__badge--muted' : ''}`}
        aria-hidden
      >
        {active ? <Spinner size="sm" animation="border" /> : <i className={iconClass} />}
      </span>
      <span className="sample-zip-gate-choice__label">{label}</span>
      <span className="sample-zip-gate-choice__desc">{desc}</span>
    </button>
  );
}

function resolvePreviousEntry(
  resumeFileName: string | null,
  lastZipName: string | null,
  recent: LocalZipRecentEntry[]
): LocalZipRecentEntry | null {
  if (resumeFileName) {
    return (
      recent.find((e) => e.name === resumeFileName) ?? {
        name: resumeFileName,
        path: null,
        updatedAt: '',
      }
    );
  }
  if (recent[0]) return recent[0];
  if (lastZipName) return { name: lastZipName, path: null, updatedAt: '' };
  return null;
}

export default function SampleZipGate({ onConnected, onError }: SampleZipGateProps) {
  const { t } = useTranslation();
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const [busyAction, setBusyAction] = useState<ZipGateAction>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [resumeFileName, setResumeFileName] = useState<string | null>(null);
  const [lastZipName] = useState(() => getLastSampleZipFileName());
  const [recentEntries] = useState(() => {
    migrateLegacyLastZipFileName('sample-manager', 'sample-manager.lastZipFileName');
    return listLocalZipRecent('sample-manager');
  });
  const fsaa = isSampleFsaaSupported();
  const busy = busyAction != null;
  const canSilentResume = !!resumeFileName;
  const previousEntry = resolvePreviousEntry(resumeFileName, lastZipName, recentEntries);
  const previousName = previousEntry?.name ?? null;
  const showContinue = !!previousName;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await Promise.race([
        tryRestoreSampleZipSession(),
        new Promise<'none'>((resolve) => {
          window.setTimeout(() => resolve('none'), 2500);
        }),
      ]);
      if (cancelled) return;
      if (result === 'restored') {
        onConnectedRef.current();
        setBootstrapping(false);
        return;
      }
      const peek = await Promise.race([
        peekStoredSampleZipResume(),
        new Promise<null>((resolve) => {
          window.setTimeout(() => resolve(null), 1500);
        }),
      ]);
      if (!cancelled) {
        setResumeFileName(peek?.fileName ?? null);
        setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const run = async (
    action: Exclude<ZipGateAction, null>,
    fn: () => Promise<void>,
    options?: { allowLongPick?: boolean }
  ) => {
    setBusyAction(action);
    try {
      // ファイル選択ダイアログ中はタイムアウトしない（キャンセルは pick 側で即 resolve）
      if (options?.allowLongPick) {
        await fn();
      } else {
        await Promise.race([
          fn(),
          new Promise<never>((_, reject) => {
            window.setTimeout(() => reject(new Error('zip_gate_timeout')), 20_000);
          }),
        ]);
      }
      onConnected();
    } catch (e) {
      const msg = getSampleZipApiError(e, t);
      if ((e as Error).message !== 'zip_pick_cancelled' && msg) {
        onError(msg);
      }
      if ((e as Error).message === 'zip_no_stored_handle') {
        setResumeFileName(null);
      }
    } finally {
      setBusyAction(null);
    }
  };

  const onContinue = () => {
    if (canSilentResume) {
      void run('continue', resumeLastSampleZip);
      return;
    }
    void run('continue', openExistingSampleZip, { allowLongPick: true });
  };

  if (bootstrapping) {
    return (
      <section className="sample-zip-gate sample-zip-gate--booting" aria-busy="true" aria-live="polite">
        <div className="d-flex align-items-center gap-2 text-muted small">
          <Spinner size="sm" animation="border" />
          {t('sample.zip_gate_restoring', { defaultValue: '前回の Zip を確認しています…' })}
        </div>
      </section>
    );
  }

  return (
    <section className="sample-zip-gate" aria-labelledby="sample-zip-gate-title">
      <h2 id="sample-zip-gate-title" className="sample-zip-gate__title">
        {t('sample.zip_gate_title', { defaultValue: 'サンプル管理（ローカル Zip）' })}
      </h2>
      <p className="sample-zip-gate__privacy">
        {t('sample.zip_gate_privacy', {
          defaultValue: 'PC 上の Zip が正本 · サーバー非保存',
        })}
        {fsaa
          ? ` · ${t('sample.zip_gate_save_hint', {
              defaultValue: 'Chrome / Edge では選択ファイルへ自動保存',
            })}`
          : ` · ${t('sample.zip_gate_fsaa_hint', {
              defaultValue: '保存は「Zip をダウンロード」',
            })}`}
      </p>

      {recentEntries.length > 0 ? (
        <LocalZipRecentList
          entries={recentEntries}
          heading={t('common.local_zip_recent_heading', {
            defaultValue: '最近使った Zip（パス付き）',
          })}
        />
      ) : null}

      <div
        className={`sample-zip-gate__choices${showContinue ? ' sample-zip-gate__choices--with-continue' : ''}`}
      >
        {showContinue && previousEntry && (
          <SampleZipGateChoice
            action="continue"
            busyAction={busyAction}
            busy={busy}
            primary
            className="sample-zip-gate-choice--continue"
            iconClass="fas fa-history"
            label={
              <LocalZipFileIdentity
                name={previousEntry.name}
                path={previousEntry.path}
                hint={t('common.local_zip_continue_hint', { defaultValue: '前回の続き' })}
              />
            }
            desc={
              canSilentResume
                ? t('sample.zip_gate_continue_desc_resume', {
                    defaultValue: '前回のファイルをそのまま開く',
                  })
                : t('sample.zip_gate_continue_desc_pick', {
                    defaultValue: '同じ Zip を選び直して開く',
                  })
            }
            onClick={onContinue}
          />
        )}

        <SampleZipGateChoice
          action="open"
          busyAction={busyAction}
          busy={busy}
          primary={!showContinue}
          mutedBadge={showContinue}
          iconClass="fas fa-folder-open"
          label={
            showContinue
              ? t('sample.zip_gate_open_other', { defaultValue: '別の Zip を開く' })
              : t('sample.zip_gate_open', { defaultValue: '既存の Zip を開く' })
          }
          desc={t('sample.zip_gate_step_existing', {
            defaultValue: 'sample-manager.zip を選択',
          })}
          onClick={() => void run('open', openExistingSampleZip, { allowLongPick: true })}
        />

        <SampleZipGateChoice
          action="create"
          busyAction={busyAction}
          busy={busy}
          mutedBadge
          iconClass="fas fa-plus"
          label={t('sample.zip_gate_create', { defaultValue: '新規 Zip で開始' })}
          desc={t('sample.zip_gate_step_new', {
            defaultValue: '空の作業コピーを開始（保存はダウンロード）',
          })}
          onClick={() => void run('create', createNewSampleZip)}
        />
      </div>

      {showContinue && previousName ? (
        <p className="sample-zip-gate__note">
          {canSilentResume
            ? t('sample.zip_gate_note_resume', {
                defaultValue: '権限が残っていればワンクリックで再開できます。',
              })
            : t('sample.zip_gate_note_continue_pick', {
                name: previousName,
                defaultValue: '{{name}} をファイル選択で開いてください。',
              })}
        </p>
      ) : (
        <p className="sample-zip-gate__note">
          {t('sample.zip_gate_note', {
            defaultValue: '共同利用・帳票・共有URLはログイン後のクラウド DB で利用できます。',
          })}
        </p>
      )}
    </section>
  );
}
